import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Loader2, ArrowLeft, Upload, X, Send, Info } from 'lucide-react';
import { toast } from 'sonner';
import {
  blogsAPI,
  CATEGORY_LABELS,
  getCurrentAuthorRole,
  resolveImageUrl,
} from '../services/blogsApi';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_COVER_MB = 5;

const BlogEditorPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(id);
  const authToken =
    localStorage.getItem('session_token') ||
    localStorage.getItem('adminToken') ||
    localStorage.getItem('hostToken');
  const role = getCurrentAuthorRole();

  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('destination-guide');
  const [destination, setDestination] = useState('');
  const [tagsRaw, setTagsRaw] = useState('');
  const [body, setBody] = useState('');
  const [coverImage, setCoverImage] = useState(null);
  const [coverUploading, setCoverUploading] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [initialStatus, setInitialStatus] = useState(null);
  const [rejectionReason, setRejectionReason] = useState(null);
  const quillRef = useRef(null);
  const coverInputRef = useRef(null);

  // Redirect unauthenticated users
  useEffect(() => {
    if (!authToken) {
      toast.info('Please sign in to write a blog');
      navigate('/login', { state: { from: '/blog/new' }, replace: true });
    }
  }, [authToken, navigate]);

  // Load existing blog for editing
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      try {
        const mine = await blogsAPI.mine();
        const found = (mine.data || []).find((b) => b.id === id);
        if (!found) {
          toast.error('Blog not found or not yours to edit');
          navigate('/blog/mine', { replace: true });
          return;
        }
        // Refetch full body
        const detail = await axios.get(`${BACKEND_URL}/api/blogs/${found.slug || found.id}`).catch(() => null);
        const full = detail?.data?.data || found;
        if (cancelled) return;
        setTitle(full.title || '');
        setCategory(full.category || 'destination-guide');
        setDestination(full.destination || '');
        setTagsRaw((full.tags || []).join(', '));
        setBody(full.body_html || '');
        setCoverImage(full.cover_image || null);
        setInitialStatus(full.status);
        setRejectionReason(full.rejection_reason);
      } catch (_e) {
        toast.error('Could not load blog to edit');
        navigate('/blog/mine', { replace: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit, navigate]);

  // Custom image handler for Quill toolbar — uploads inline images
  const handleQuillImage = () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const res = await blogsAPI.uploadImage(file);
        if (res.success && res.url) {
          const url = resolveImageUrl(res.url);
          const quill = quillRef.current?.getEditor?.();
          if (quill) {
            const range = quill.getSelection(true);
            quill.insertEmbed(range.index, 'image', url, 'user');
            quill.setSelection(range.index + 1);
          }
        }
      } catch (e) {
        toast.error('Failed to upload image');
      }
    };
  };

  // Convert a YouTube URL to an embed URL (or return null if we can't).
  const toYouTubeEmbed = (url) => {
    try {
      const u = new URL(url);
      if (u.hostname.includes('youtube.com')) {
        const v = u.searchParams.get('v');
        if (v) return `https://www.youtube.com/embed/${v}`;
        if (u.pathname.startsWith('/embed/')) return `https://www.youtube.com${u.pathname}`;
      }
      if (u.hostname === 'youtu.be') {
        return `https://www.youtube.com/embed${u.pathname}`;
      }
    } catch (_e) {
      // fall through
    }
    return null;
  };

  const handleQuillVideo = () => {
    const url = window.prompt('Paste a YouTube video URL:');
    if (!url) return;
    const embed = toYouTubeEmbed(url.trim());
    if (!embed) {
      toast.error('Only YouTube video URLs are supported for now');
      return;
    }
    const quill = quillRef.current?.getEditor?.();
    if (!quill) return;
    const range = quill.getSelection(true);
    const iframeHtml = `<div class="blog-embed"><iframe src="${embed}" width="560" height="315" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen title="YouTube video"></iframe></div>`;
    quill.clipboard.dangerouslyPasteHTML(range.index, iframeHtml);
    quill.setSelection(range.index + 1);
  };

  const handleQuillMap = () => {
    const url = window.prompt(
      'Paste a Google Maps embed URL (from Google Maps → Share → Embed a map):'
    );
    if (!url) return;
    const trimmed = url.trim();
    // Accept only the embed variants Google actually issues; sanitizer will double-check
    if (!/^https:\/\/(www\.)?google\.com\/maps\/(embed|d\/embed)/.test(trimmed) &&
        !/^https:\/\/maps\.google\.com\/maps/.test(trimmed)) {
      toast.error('That URL doesn\'t look like a Google Maps embed link');
      return;
    }
    const quill = quillRef.current?.getEditor?.();
    if (!quill) return;
    const range = quill.getSelection(true);
    const iframeHtml = `<div class="blog-embed"><iframe src="${trimmed}" width="600" height="450" frameborder="0" allowfullscreen loading="lazy" referrerpolicy="no-referrer-when-downgrade" title="Google Map"></iframe></div>`;
    quill.clipboard.dangerouslyPasteHTML(range.index, iframeHtml);
    quill.setSelection(range.index + 1);
  };

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [2, 3, false] }],
          ['bold', 'italic', 'underline'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['blockquote', 'link', 'image', 'video'],
          ['clean'],
        ],
        handlers: {
          image: handleQuillImage,
          video: handleQuillVideo,
        },
      },
    }),
    [],
  );

  const handleCoverPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_COVER_MB * 1024 * 1024) {
      toast.error(`Cover must be under ${MAX_COVER_MB}MB`);
      return;
    }
    try {
      setCoverUploading(true);
      const res = await blogsAPI.uploadImage(file);
      if (res.success) {
        setCoverImage(res.url);
        toast.success('Cover image uploaded');
      }
    } catch (_e) {
      toast.error('Failed to upload cover');
    } finally {
      setCoverUploading(false);
    }
  };

  const wordCount = useMemo(() => {
    const text = body.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    return text ? text.split(' ').length : 0;
  }, [body]);

  const validate = () => {
    if (title.trim().length < 6) {
      toast.error('Title needs at least 6 characters');
      return false;
    }
    const text = body.replace(/<[^>]+>/g, '').trim();
    if (text.length < 40) {
      toast.error('Blog body needs at least 40 characters of content');
      return false;
    }
    if (!category) {
      toast.error('Please pick a category');
      return false;
    }
    return true;
  };

  const submit = async () => {
    if (!validate()) return;
    const tags = tagsRaw
      .split(/[,#]/)
      .map((t) => t.trim().replace(/^#/, ''))
      .filter(Boolean)
      .slice(0, 10);
    const payload = {
      title: title.trim(),
      body_html: body,
      category,
      cover_image: coverImage || null,
      destination: destination.trim() || null,
      tags,
    };
    try {
      setSubmitting(true);
      let res;
      if (isEdit) {
        res = await blogsAPI.update(id, payload);
      } else {
        res = await blogsAPI.create(payload);
      }
      if (res.success) {
        if (res.pending_review) {
          toast.success(
            'Your blog has been submitted for admin review. You will be notified once it goes live.',
            { duration: 6000 },
          );
        } else {
          toast.success(isEdit ? 'Blog updated & published' : 'Blog published');
        }
        navigate('/blog/mine');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save blog');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-[#7CB342]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl pt-6">
        <Button variant="ghost" onClick={() => navigate(-1)} className="text-gray-600 mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>

        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            {isEdit ? 'Edit Blog' : 'Write a Blog'}
          </h1>
          {role && (
            <span
              className={`text-xs px-2 py-1 rounded-full ${
                role === 'admin'
                  ? 'bg-emerald-100 text-emerald-800'
                  : role === 'host'
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-gray-100 text-gray-700'
              }`}
            >
              Publishing as {role === 'admin' ? 'GeoStays Team' : role === 'host' ? 'Host' : 'Traveler'}
            </span>
          )}
        </div>

        {role !== 'admin' && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-900">
              Your blog will be reviewed by our team before going live to keep quality high.
              You&apos;ll be notified once it&apos;s approved (usually within 24 hours).
            </div>
          </div>
        )}

        {isEdit && initialStatus === 'rejected' && rejectionReason && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-sm text-red-800" data-testid="rejection-notice">
            <div className="font-semibold mb-1">Previous submission rejected</div>
            <div>Admin note: {rejectionReason}</div>
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Story Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div>
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="mt-2"
                placeholder="e.g., 7 Hidden Homestays in the Himalayas You'll Fall in Love With"
                maxLength={180}
                data-testid="blog-title-input"
              />
              <div className="text-xs text-gray-400 mt-1">{title.length}/180</div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Category *</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="mt-2" data-testid="blog-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATEGORY_LABELS).map(([slug, label]) => (
                      <SelectItem key={slug} value={slug}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="destination">Destination (optional)</Label>
                <Input
                  id="destination"
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  placeholder="e.g., Munnar, Kerala"
                  className="mt-2"
                  data-testid="blog-destination-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="tags">Tags (comma-separated, up to 10)</Label>
              <Input
                id="tags"
                value={tagsRaw}
                onChange={(e) => setTagsRaw(e.target.value)}
                placeholder="e.g., kerala, homestay, eco-travel"
                className="mt-2"
                data-testid="blog-tags-input"
              />
              <div className="text-xs text-gray-400 mt-1">
                Tags help travellers discover your story via search.
              </div>
            </div>

            <div>
              <Label>Cover Image</Label>
              <div className="mt-2">
                {coverImage ? (
                  <div className="relative rounded-lg overflow-hidden bg-gray-100 aspect-[16/9] max-w-lg">
                    <img
                      src={resolveImageUrl(coverImage)}
                      alt="cover"
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => setCoverImage(null)}
                      className="absolute top-2 right-2 bg-white/90 rounded-full p-1 shadow"
                      data-testid="remove-cover-btn"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => coverInputRef.current?.click()}
                    disabled={coverUploading}
                    className="w-full max-w-lg aspect-[16/9] rounded-lg border-2 border-dashed border-gray-300 hover:border-[#7CB342] hover:bg-emerald-50/40 transition-all flex flex-col items-center justify-center text-gray-500"
                    data-testid="upload-cover-btn"
                  >
                    {coverUploading ? (
                      <Loader2 className="h-6 w-6 animate-spin" />
                    ) : (
                      <>
                        <Upload className="h-6 w-6 mb-1" />
                        <span className="text-sm">Upload a cover image</span>
                        <span className="text-xs text-gray-400 mt-1">Recommended: 1600×900, under {MAX_COVER_MB}MB</span>
                      </>
                    )}
                  </button>
                )}
                <input
                  ref={coverInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleCoverPick}
                  className="hidden"
                  data-testid="cover-file-input"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label>Body *</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleQuillMap}
                  className="text-xs h-7"
                  data-testid="embed-gmap-btn"
                >
                  📍 Embed Google Map
                </Button>
              </div>
              <div className="mt-2 bg-white rounded-lg overflow-hidden" data-testid="blog-body-editor">
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={body}
                  onChange={setBody}
                  modules={modules}
                  placeholder="Write your story… Share what you saw, felt, tasted, and what other travellers should know."
                  style={{ minHeight: 320 }}
                />
              </div>
              <div className="text-xs text-gray-400 mt-2">{wordCount} words</div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={() => navigate(-1)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className="bg-[#7CB342] hover:bg-[#6da036]"
            data-testid="submit-blog-btn"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            {role === 'admin'
              ? isEdit
                ? 'Update & Publish'
                : 'Publish now'
              : isEdit
              ? 'Save & Resubmit for Review'
              : 'Submit for Review'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BlogEditorPage;
